import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';

export interface MediaItem {
  type: 'image' | 'video';
  url: string;
  alt?: string;
  poster?: string;
  caption?: string;
}

interface MediaCarouselProps {
  items: MediaItem[];
  autoPlayInterval?: number;
  showControls?: boolean;
  className?: string;
}

export default function MediaCarousel({
  items,
  autoPlayInterval = 5000,
  showControls = true,
  className = ''
}: MediaCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState<Record<number, boolean>>({});
  const [isMuted, setIsMuted] = useState<Record<number, boolean>>({});
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({});
  const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentItem = items[currentIndex];
  const isVideo = currentItem?.type === 'video';

  const clearAutoPlayTimer = useCallback(() => {
    if (autoPlayTimerRef.current) {
      clearTimeout(autoPlayTimerRef.current);
      autoPlayTimerRef.current = null;
    }
  }, []);

  const startAutoPlayTimer = useCallback(() => {
    if (!isAutoPlaying || items.length <= 1) return;

    clearAutoPlayTimer();

    const currentVideoPlaying = isVideo && isPlaying[currentIndex];
    if (currentVideoPlaying) return;

    autoPlayTimerRef.current = setTimeout(() => {
      goToNext();
    }, autoPlayInterval);
  }, [isAutoPlaying, items.length, isVideo, isPlaying, currentIndex, autoPlayInterval]);

  useEffect(() => {
    startAutoPlayTimer();
    return () => clearAutoPlayTimer();
  }, [startAutoPlayTimer, clearAutoPlayTimer]);

  const pauseAllVideos = useCallback(() => {
    Object.values(videoRefs.current).forEach(video => {
      if (video && !video.paused) {
        video.pause();
      }
    });
  }, []);

  const goToNext = useCallback(() => {
    pauseAllVideos();
    setCurrentIndex((prev) => (prev + 1) % items.length);
  }, [items.length, pauseAllVideos]);

  const goToPrevious = useCallback(() => {
    pauseAllVideos();
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  }, [items.length, pauseAllVideos]);

  const goToIndex = useCallback((index: number) => {
    pauseAllVideos();
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  }, [pauseAllVideos]);

  const handleVideoPlayPause = useCallback((index: number) => {
    const video = videoRefs.current[index];
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(prev => ({ ...prev, [index]: true }));
      setIsAutoPlaying(false);
      clearAutoPlayTimer();
    } else {
      video.pause();
      setIsPlaying(prev => ({ ...prev, [index]: false }));
      setIsAutoPlaying(true);
      startAutoPlayTimer();
    }
  }, [clearAutoPlayTimer, startAutoPlayTimer]);

  const handleVideoMuteToggle = useCallback((index: number) => {
    const video = videoRefs.current[index];
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(prev => ({ ...prev, [index]: !video.muted }));
  }, []);

  const handleVideoEnded = useCallback((index: number) => {
    setIsPlaying(prev => ({ ...prev, [index]: false }));
    setIsAutoPlaying(true);
    goToNext();
  }, [goToNext]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrevious();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      goToPrevious();
    } else if (e.key === 'ArrowRight') {
      goToNext();
    }
  }, [goToPrevious, goToNext]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('keydown', handleKeyDown as any);
    return () => container.removeEventListener('keydown', handleKeyDown as any);
  }, [handleKeyDown]);

  const handleMouseEnter = () => {
    setIsAutoPlaying(false);
    clearAutoPlayTimer();
  };

  const handleMouseLeave = () => {
    if (!isVideo || !isPlaying[currentIndex]) {
      setIsAutoPlaying(true);
      startAutoPlayTimer();
    }
  };

  if (!items || items.length === 0) {
    return (
      <div className="w-full aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
        <p className="text-gray-500">No media available</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative group ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      tabIndex={0}
      role="region"
      aria-label="Media carousel"
      aria-live="polite"
    >
      <div className="relative w-full overflow-hidden rounded-lg bg-white" style={{ minHeight: '400px' }}>
        {items.map((item, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-500 ${
              index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
            aria-hidden={index !== currentIndex}
          >
            {item.type === 'image' ? (
              <img
                src={item.url}
                alt={item.alt || `Slide ${index + 1}`}
                className="w-full h-full object-contain"
                loading={index === currentIndex ? 'eager' : 'lazy'}
              />
            ) : (
              <div className="relative w-full h-full">
                <video
                  ref={(el) => {
                    videoRefs.current[index] = el;
                  }}
                  src={item.url}
                  poster={item.poster}
                  className="w-full h-full object-contain"
                  playsInline
                  preload={index === currentIndex ? 'auto' : 'none'}
                  muted={isMuted[index] !== false}
                  onEnded={() => handleVideoEnded(index)}
                  aria-label={item.alt || `Video ${index + 1}`}
                />

                {index === currentIndex && showControls && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    {!isPlaying[index] && (
                      <button
                        onClick={() => handleVideoPlayPause(index)}
                        className="bg-white/90 hover:bg-white rounded-full p-4 transition-all duration-200 hover:scale-110 shadow-lg"
                        aria-label="Play video"
                      >
                        <Play className="h-8 w-8 text-gray-900 fill-gray-900" />
                      </button>
                    )}
                  </div>
                )}

                {index === currentIndex && isPlaying[index] && showControls && (
                  <div className="absolute bottom-4 right-4 flex gap-2">
                    <button
                      onClick={() => handleVideoPlayPause(index)}
                      className="bg-white/90 hover:bg-white rounded-full p-2 transition-all duration-200 hover:scale-110 shadow-lg"
                      aria-label="Pause video"
                    >
                      <Pause className="h-5 w-5 text-gray-900" />
                    </button>
                    <button
                      onClick={() => handleVideoMuteToggle(index)}
                      className="bg-white/90 hover:bg-white rounded-full p-2 transition-all duration-200 hover:scale-110 shadow-lg"
                      aria-label={isMuted[index] ? 'Unmute video' : 'Mute video'}
                    >
                      {isMuted[index] ? (
                        <Volume2 className="h-5 w-5 text-gray-900" />
                      ) : (
                        <VolumeX className="h-5 w-5 text-gray-900" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {items.length > 1 && showControls && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110 shadow-lg z-20"
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-6 w-6 text-gray-900" />
          </button>

          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110 shadow-lg z-20"
            aria-label="Next slide"
          >
            <ChevronRight className="h-6 w-6 text-gray-900" />
          </button>
        </>
      )}

      {items.length > 1 && (
        <div className="mt-4 flex justify-center gap-2">
          {items.map((item, index) => (
            <button
              key={index}
              onClick={() => goToIndex(index)}
              className={`relative overflow-hidden rounded-lg transition-all duration-200 ${
                index === currentIndex
                  ? 'ring-2 ring-primary-500 ring-offset-2'
                  : 'opacity-60 hover:opacity-100'
              }`}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === currentIndex ? 'true' : 'false'}
              style={{ width: '80px', height: '80px' }}
            >
              {item.type === 'image' ? (
                <img
                  src={item.url}
                  alt={item.alt || `Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="relative w-full h-full bg-gray-200">
                  <video
                    src={item.url}
                    poster={item.poster}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Play className="h-6 w-6 text-white fill-white" />
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
